"use client";
import { useEffect } from "react";

import NextLink from "next/link";

import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { Image } from "@heroui/image";

import { PageViewTracker } from "@/components/observability/PageViewTracker";
import { EventKeys } from "@/lib/observability/catalog";
import { routes } from "@/lib/routes";

export default function UseCasesPage() {
  useEffect(() => {
    document.title = "Use Cases - NeedBridge";
  }, []);

  const useCases = [
    {
      ctaHref: "/register",
      ctaText: "Get Started with Foster Care Support",
      description:
        "Post specific one-time needs for foster families — from emergency supplies to single meal deliveries. Perfect for supplementing existing support systems with immediate, tangible assistance.",
      emailTheme: "Ocean Blue",
      features: [
        "Emergency clothing and supplies for new placements",
        "One-time meal deliveries during transitions",
        "Specific household items (cribs, car seats, etc.)",
        "Single transportation rides for appointments",
        "Immediate financial assistance for urgent needs",
      ],
      highlightFeatures: [
        "Critical need instant blasts for urgent placements",
        "Custom areas by county for regional teams",
        "Caseworker approval workflow for all posted needs",
      ],
      id: "foster-care-heading",
      image: "/images/foster-care.jpg",
      imageAlt: "Foster family support",
      title: "Foster Family Support",
    },
    {
      ctaHref: "/register",
      ctaText: "Start Your Benevolence Program",
      description:
        "Organize needs like bill assistance, groceries, or gas cards for individuals in crisis — and allow trusted volunteers or donors to meet them.",
      emailTheme: "Forest Green",
      features: [
        "Utility bill assistance",
        "Grocery and food support",
        "Gas cards and transportation help",
        "Rent and housing assistance",
        "Medical and prescription support",
      ],
      highlightFeatures: [
        'Custom terminology: call volunteers "helpers"',
        "Embeddable forms on your church website",
        "In-kind donation receipts for tax compliance",
      ],
      id: "church-benevolence-heading",
      image: "/images/church-benevolence.jpg",
      imageAlt: "Church benevolence",
      title: "Church Benevolence Requests",
    },
    {
      ctaHref: "/register",
      ctaText: "Set Up Emergency Response",
      description:
        "Quickly mobilize people for cleanup, supplies, housing, or food after a storm, fire, or other local emergency.",
      emailTheme: "Sunset Orange",
      features: [
        "Emergency shelter coordination",
        "Cleanup and debris removal",
        "Supply distribution",
        "Meal preparation and delivery",
        "Transportation assistance",
      ],
      highlightFeatures: [
        "Critical need blasts for immediate mobilization",
        "Public needs page for community-wide visibility",
        "No-login claiming removes all friction for new volunteers",
      ],
      id: "disaster-relief-heading",
      image: "/images/disaster-relief.jpg",
      imageAlt: "Disaster relief",
      title: "Disaster Relief & Emergency Response",
    },
    {
      ctaHref: "/register",
      ctaText: "Start Coordinating Support",
      description:
        "Community Action agencies serve diverse populations across large geographies. NeedBridge helps coordinate resources across programs and service areas.",
      emailTheme: "Ocean Blue",
      features: [
        "Multi-program need tracking",
        "Cross-county need sharing",
        "Resource distribution management",
        "Client assistance documentation",
        "Impact reporting for grants and funders",
      ],
      highlightFeatures: [
        "Custom areas by county or service region",
        "Role-based access for different programs",
        "API integration with existing case management",
      ],
      id: "community-action-heading",
      image: "/images/community-action.svg",
      imageAlt:
        "Community action agency coordinating resources across counties",
      title: "Community Action Agencies",
    },
    {
      ctaHref: "/register",
      ctaText: "Set Up School Support",
      description:
        "School districts can connect families with community resources — from school supplies to holiday assistance — coordinated through counselors and social workers.",
      emailTheme: "Sunset Orange",
      features: [
        "School supply drives and back-to-school needs",
        "Holiday assistance coordination",
        "Family emergency support",
        "After-school program resources",
        "Teacher classroom supply requests",
      ],
      highlightFeatures: [
        'Custom terminology: "districts" instead of "areas"',
        "Embeddable needs browser on school website",
        "Volunteer email subscriptions by school zone",
      ],
      id: "school-district-heading",
      image: "/images/school-district.svg",
      imageAlt: "School district connecting families with community resources",
      title: "School District Support Programs",
    },
  ];

  return (
    <>
      <PageViewTracker
        data={{ section: "use-cases" }}
        eventKey={EventKeys.PUBLIC_USE_CASES_PAGE_VIEW}
      />

      {/* Hero Section */}
      <section
        aria-labelledby="use-cases-heading"
        className="text-center mb-16"
      >
        <h1
          className="text-4xl md:text-5xl font-bold mb-6"
          id="use-cases-heading"
        >
          Use Cases
        </h1>
        <p className="text-xl text-default-600 max-w-3xl mx-auto">
          Discover how organizations use NeedBridge to coordinate volunteers and
          make a real impact in their communities.
        </p>
      </section>

      {/* Use Cases */}
      <section aria-labelledby="use-cases-grid-heading" className="mb-16">
        <h2 className="sr-only" id="use-cases-grid-heading">
          Use Cases
        </h2>
        <div className="space-y-12">
          {useCases.map((useCase, index) => (
            <Card key={useCase.title} className="overflow-hidden">
              <div
                className={`flex flex-col ${index % 2 === 1 ? "lg:flex-row-reverse" : "lg:flex-row"}`}
              >
                {/* Image side */}
                <div className="lg:w-2/5 relative">
                  <Image
                    alt={useCase.imageAlt}
                    className="w-full h-full object-cover min-h-[200px]"
                    fallbackSrc="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23f0f0f0' width='400' height='300'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='sans-serif' font-size='16'%3EUse Case%3C/text%3E%3C/svg%3E"
                    loading="lazy"
                    radius="none"
                    src={useCase.image}
                  />
                </div>

                {/* Content side */}
                <div className="lg:w-3/5">
                  <CardHeader className="pb-0 pt-6 px-6">
                    <div>
                      <h3 className="text-2xl font-bold" id={useCase.id}>
                        {useCase.title}
                      </h3>
                      <span className="text-xs text-gray-500">
                        Email theme: {useCase.emailTheme}
                      </span>
                    </div>
                  </CardHeader>
                  <CardBody className="px-6 pb-6">
                    <p className="text-default-600 mb-4">
                      {useCase.description}
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          Common needs:
                        </p>
                        <ul className="list-disc pl-5 space-y-1 text-sm text-default-600">
                          {useCase.features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-gray-700 mb-2">
                          Key NeedBridge features:
                        </p>
                        <ul className="space-y-1 text-sm text-default-600">
                          {useCase.highlightFeatures.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-green-500 mt-0.5 flex-shrink-0">
                                ✓
                              </span>
                              {feature}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <Button
                      as={NextLink}
                      color="primary"
                      href={useCase.ctaHref}
                    >
                      {useCase.ctaText}
                    </Button>
                  </CardBody>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <section
        aria-labelledby="cta-heading"
        className="bg-primary-50 rounded-xl p-8 text-center"
      >
        <h2 className="text-3xl font-bold mb-4" id="cta-heading">
          Ready to Get Started?
        </h2>
        <p className="text-xl mb-6 max-w-2xl mx-auto">
          Start free with the Starter plan, or get guided onboarding on a paid
          plan.
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          <Button
            aria-label="Register your organization to start posting needs"
            as={NextLink}
            color="primary"
            href={routes.auth.register()}
            size="lg"
          >
            Try Free
          </Button>
          <Button
            aria-label="Learn more about how NeedBridge works"
            as={NextLink}
            href={routes.public.howItWorks()}
            size="lg"
            variant="bordered"
          >
            See How It Works
          </Button>
        </div>
      </section>
    </>
  );
}
